/**
 * @fileoverview Flight Crew Change Dead-Letter Search
 * @description Search Azure Service Bus dead-letter queue for flight crew
 *              change messages by flight number and date. Displays results
 *              as professional cards with duplicate handling.
 */

import React, { useState, useCallback } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Stack, Alert,
  Chip, CircularProgress, Fade, Grow, Divider, Tooltip, IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Search, Plane, Clock, AlertTriangle, Hash, MapPin,
  Copy, CheckCircle, Inbox, RefreshCw, ArrowRight,
} from 'lucide-react';
import { flightCrewDeadletterApi } from '../services/flightCrewDeadletterApi';

/* ── Shared sx tokens (matching Report Logs style) ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0097A9' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0097A9', borderWidth: 2,
  },
};

/* ── Copy button component ── */
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setOk(true);
    setTimeout(() => setOk(false), 1500);
  };
  return (
    <Tooltip title={ok ? 'Copied!' : 'Copy sequence #'}>
      <IconButton size="small" onClick={copy} sx={{ '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
        {ok ? <CheckCircle size={14} color="#00AF66" /> : <Copy size={14} color="#64748b" />}
      </IconButton>
    </Tooltip>
  );
}

/* ── Status chip for dead-letter reason ── */
function DLReasonChip({ reason }) {
  const config = {
    MaxDeliveryCountExceeded: { color: '#dc2626', bg: '#fef2f2', label: 'Max Delivery Exceeded' },
    HeaderSizeExceeded: { color: '#d97706', bg: '#fffbeb', label: 'Header Too Large' },
    TTLExpiredException: { color: '#7c3aed', bg: '#f5f3ff', label: 'TTL Expired' },
  };
  const c = config[reason] || { color: '#64748b', bg: '#f8fafc', label: reason || 'Unknown' };
  return (
    <Chip
      label={c.label}
      size="small"
      icon={<AlertTriangle size={12} />}
      sx={{
        bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: '0.7rem',
        border: `1px solid ${c.color}22`,
        '& .MuiChip-icon': { color: c.color },
      }}
    />
  );
}

/* ── Duplicate indicator ── */
function DuplicateBadge({ count }) {
  if (count <= 1) return null;
  return (
    <Chip
      label={`${count} occurrences`}
      size="small"
      sx={{
        bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600,
        fontSize: '0.7rem', border: '1px solid #2563eb22',
      }}
    />
  );
}

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const FlightCrewDeadletter = () => {
  const [flightNumber, setFlightNumber] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleSearch = useCallback(async () => {
    if (!flightNumber.trim() || !date.trim()) {
      setError('Please enter both flight number and date.');
      return;
    }

    setError('');
    setResults(null);
    setLoading(true);

    try {
      const res = await flightCrewDeadletterApi.search(flightNumber.trim(), date.trim());
      if (res.error) {
        setError(res.error);
      }
      setResults(res);
    } catch (err) {
      setError(err.message || 'Failed to search dead-letter queue.');
    } finally {
      setLoading(false);
    }
  }, [flightNumber, date]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Group matches by sequence number to count duplicates
  const groupedMatches = results?.matches?.reduce((acc, m) => {
    const key = m.sequence_number;
    if (!acc[key]) acc[key] = { ...m, _count: 1 };
    else acc[key]._count++;
    return acc;
  }, {});
  const matchList = groupedMatches ? Object.values(groupedMatches) : [];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: '16px',
            background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <Inbox size={28} />
            <Typography variant="h5" fontWeight={700}>
              Flight Crew Change — Dead-Letter Search
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Search the Azure Service Bus dead-letter queue for undelivered flight crew change messages.
            Enter a flight number and date to find matching messages that failed processing.
          </Typography>
        </Paper>
      </Fade>

      {/* Search Form */}
      <Fade in timeout={600}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Flight Number"
              placeholder="e.g. 570"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              sx={{ ...inputSx, minWidth: 160 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Plane size={16} color="#64748b" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Flight Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              sx={{ ...inputSx, minWidth: 180 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Clock size={16} color="#64748b" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search size={16} />}
              sx={{
                bgcolor: '#0097A9', borderRadius: '10px', px: 3, py: 1,
                textTransform: 'none', fontWeight: 600, minWidth: 120,
                '&:hover': { bgcolor: '#007f8f' },
              }}
            >
              {loading ? 'Searching...' : 'Search DLQ'}
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Fade>

      {/* Loading State */}
      {loading && (
        <Fade in>
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '14px', border: '1px solid #e5e7eb' }}>
            <CircularProgress size={40} sx={{ color: '#0097A9', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Scanning dead-letter queue... This may take a moment for large queues.
            </Typography>
          </Paper>
        </Fade>
      )}

      {/* Results */}
      {results && !loading && (
        <Fade in timeout={500}>
          <Box>
            {/* Summary Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 1,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  icon={<Hash size={14} />}
                  label={`${results.total_scanned.toLocaleString()} messages scanned`}
                  size="small"
                  sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }}
                />
                <Chip
                  icon={results.match_count > 0 ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                  label={results.match_count > 0
                    ? `${results.match_count} match${results.match_count > 1 ? 'es' : ''} found`
                    : 'No matches found'}
                  size="small"
                  sx={{
                    bgcolor: results.match_count > 0 ? '#f0fdf4' : '#fef2f2',
                    color: results.match_count > 0 ? '#16a34a' : '#dc2626',
                    fontWeight: 600,
                  }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Queue: flightcrewchange-inbound
              </Typography>
            </Paper>

            {/* Match Cards */}
            {matchList.length > 0 && (
              <Stack spacing={2}>
                {matchList.map((match, idx) => (
                  <Grow in key={match.sequence_number} timeout={300 + idx * 100}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0, borderRadius: '14px', border: '1px solid #e5e7eb',
                        overflow: 'hidden', transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)' },
                      }}
                    >
                      {/* Card Header */}
                      <Box
                        sx={{
                          px: 2.5, py: 1.5,
                          background: 'linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)',
                          borderBottom: '1px solid #e5e7eb',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          flexWrap: 'wrap', gap: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 36, height: 36, borderRadius: '10px',
                              bgcolor: '#0097A9', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <Plane size={18} color="#fff" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="#01426A">
                              {match.carrier} {match.flight_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Seq #{match.sequence_number}
                              <CopyBtn text={String(match.sequence_number)} />
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DLReasonChip reason={match.dead_letter_reason} />
                          <DuplicateBadge count={match._count} />
                        </Stack>
                      </Box>

                      {/* Card Body */}
                      <Box sx={{ px: 2.5, py: 2 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 1.5, sm: 4 }}
                          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                        >
                          <DetailItem icon={<Clock size={14} />} label="Enqueued" value={formatDateTime(match.enqueued_at)} />
                          <DetailItem icon={<MapPin size={14} />} label="Origin" value={match.start_station || '—'} />
                          <DetailItem icon={<Hash size={14} />} label="Flights in Message" value={match.total_flights_in_message} />
                          <DetailItem icon={<ArrowRight size={14} />} label="UDOR" value={formatDate(match.udor)} />
                        </Stack>

                        {match.dead_letter_description && (
                          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '0.8rem' }}>
                            {match.dead_letter_description}
                          </Alert>
                        )}

                        {/* Sibling flights in the same message */}
                        {match.total_flights_in_message > 1 && match.all_flights_in_message && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom>
                              Other flights in this message:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                              {match.all_flights_in_message
                                .filter(f => f.flightNumber !== match.flight_number)
                                .slice(0, 15)
                                .map((f, i) => (
                                  <Chip
                                    key={i}
                                    label={`${f.carrier} ${f.flightNumber}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                  />
                                ))}
                              {match.all_flights_in_message.length > 16 && (
                                <Chip
                                  label={`+${match.all_flights_in_message.length - 16} more`}
                                  size="small"
                                  sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#f1f5f9' }}
                                />
                              )}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grow>
                ))}
              </Stack>
            )}

            {/* No matches state */}
            {results.match_count === 0 && (
              <Paper
                elevation={0}
                sx={{ p: 4, textAlign: 'center', borderRadius: '14px', border: '1px solid #e5e7eb' }}
              >
                <Inbox size={48} color="#94a3b8" />
                <Typography variant="h6" color="text.secondary" mt={1.5}>
                  No Matches Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Flight {flightNumber} on {date} was not found in any of the {results.total_scanned.toLocaleString()} dead-letter messages.
                </Typography>
              </Paper>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

/* ── Helper Components ── */
function DetailItem({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ color: '#64748b', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="#1f2937">
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

/* ── Formatters ── */
function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  });
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return isoStr.split('T')[0];
}

export default FlightCrewDeadletter;
